import {
  Form,
  DatePicker,
  Table,
  ConfigProvider,
  Row,
  Col,
  Button,
  Statistic,
  Card,
  Tag,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, Suspense } from 'react';
import { getEndOfDayReport } from '../../../../store/requests/reports';
import 'moment/locale/tr';
import trTR from 'antd/es/locale/tr_TR';
import { FilterOutlined } from '@ant-design/icons';
import {
  END_OF_DATE_REPORT_PAYMENT_COLORS,
  END_OF_DATE_REPORT_PAYMENT_TYPES,
  END_OF_DATE_REPORT_SUMMARY_FIELDS,
  ORDER_STATUS_COLORS,
  ORDER_STATUSES,
} from '../../../../helpers/constants';
import { Loading } from '../../../../components';
import { useSelector } from 'react-redux';
import { getStores } from '../../../../store/requests/definitions';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';

const EndOfDayReportFilters = React.lazy(
  () => import('./EndOfDayReportFilters'),
);

const EndOfDayReport = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [fetching, setFetching] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [filterHeaders, setFilterHeaders] = useState({});
  const selectedActiveStoreCode =
    useSelector((state) => state.auth?.user?.activeStore?.code) ?? null;
  const selectedRegionId =
    useSelector((state) => +state.auth?.user?.activeRegion?.id) ?? null;
  const stores =
    useSelector((state) => state.definitions?.stores)?._embedded
      ?.storeResponseDtoList ?? [];
  const [isStoresFetching, setIsStoresFetching] = useState(false);
  const [endOfDayReportForm, setEndOfDayReportForm] = useState({
    startDate: null,
    endDate: null,
    storeCode: selectedActiveStoreCode,
  });

  useEffect(() => {
    if (
      !Object.values(endOfDayReportForm).some(
        (v) => v === null || v === undefined,
      )
    ) {
      const payload = {
        ...endOfDayReportForm,
        ...filters,
      };
      setFetching(true);
      getEndOfDayReport(payload)
        .then((res) => {
          if (res.isSuccess) {
            setReportData(res.data ?? {});
          }
        })
        .finally(() => {
          setFetching(false);
          setIsFiltersOpen(false);
        });
    }
  }, [endOfDayReportForm, JSON.stringify(filters)]);

  useEffect(() => {
    if (selectedRegionId) {
      setIsStoresFetching(true);
      getStores(
        `filter=region.id==${selectedRegionId} and active==true&size=2000`,
      ).finally(() => setIsStoresFetching(false));
    }
  }, [selectedRegionId]);

  const datepickerChangeHandler = (dates, datesString) => {
    const [startDate, endDate] = datesString;
    setEndOfDayReportForm((prevState) => ({
      ...prevState,
      startDate,
      endDate,
    }));
  };

  const orderStatusFilterHandler = Object.keys(ORDER_STATUSES).map((order) => {
    return { text: t(order), value: order };
  });

  const paymentTypeFilterHandler = Object.keys(
    END_OF_DATE_REPORT_PAYMENT_TYPES,
  ).map((paymentType) => {
    return { text: t(paymentType), value: paymentType };
  });

  const paymentTypeOnFilter = (value, record) => {
    var filter = record.paymentType === value ? true : false;
    return filter;
  };

  const columns = [
    {
      title: t('orderId'),
      dataIndex: 'customerOrderNumber',
      key: 'customerOrderNumber',
      sorter: (a, b) => a.customerOrderNumber - b.customerOrderNumber,
      showSorterTooltip: false,
    },
    {
      title: t('customerName'),
      dataIndex: 'customerFullName',
      key: 'customerName',
      sorter: (a, b) => a.customerFullName.localeCompare(b.customerFullName),
      showSorterTooltip: false,
    },
    {
      title: t('deliveryDate'),
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      sorter: (a, b) => new Date(a.deliveryDate) - new Date(b.deliveryDate),
      showSorterTooltip: false,
    },
    {
      title: t('slotTime'),
      dataIndex: 'timeSlot',
      key: 'slotTime',
      sorter: (a, b) => +a.timeSlot.slice(0, 2) - +b.timeSlot.slice(0, 2),
      showSorterTooltip: false,
    },
    {
      title: t('shipmentStatus'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={ORDER_STATUS_COLORS[status]}>{t(status)}</Tag>
      ),
      filters: orderStatusFilterHandler,
      onFilter: (value, record) => record.status.startsWith(value),
      filterSearch: true,
      showSorterTooltip: false,
    },
    {
      title: t('courierName'),
      dataIndex: 'courierFullName',
      key: 'courierFullName',
      sorter: (a, b) => a.courierFullName?.localeCompare(b.courierFullName),
      showSorterTooltip: false,
    },
    {
      title: t('paymentType'),
      dataIndex: 'paymentType',
      key: 'paymentType',
      render: (type) => (
        <Tag color={END_OF_DATE_REPORT_PAYMENT_COLORS[type]}>{t(type)}</Tag>
      ),
      filters: paymentTypeFilterHandler,
      onFilter: paymentTypeOnFilter,
      filterSearch: true,
      showSorterTooltip: false,
    },
    {
      title: t('price'),
      dataIndex: 'price',
      key: 'price',
      render: (price) => `${price} ₺`,
      sorter: (a, b) => a.price - b.price,
      showSorterTooltip: false,
    },
  ];

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        if (destinationItem === 'storeCode') {
          editedFilters['region'] = undefined;
        }
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, index) => {
      if (filters[item] && item !== 'region') {
        if (item === 'storeCode') {
        }
        return (
          <Tag
            key={index}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {item === 'storeCode'
              ? `${t('store')} : ${t(filters[item])}`
              : item === 'courierId'
                ? `${t('courierName')} : ${t(filterHeaders[item])}`
                : `${t(item)} : ${t(filters[item])}`}
          </Tag>
        );
      }
    });
  };

  const renderSummaryBoxes = () => {
    return (
      <Row justify="space-between">
        {Object.keys(END_OF_DATE_REPORT_SUMMARY_FIELDS).map(
          (dataEnum, index) => {
            const field = END_OF_DATE_REPORT_SUMMARY_FIELDS[dataEnum];
            return (
              <Col key={index} flex={1} style={{ marginRight: 10, marginLeft: 10, marginBottom: 20 }}>
                <Card style={{ borderRadius: 6 }} >
                  <Statistic
                    title={t(field)}
                    value={reportData[field]}
                    valueStyle={{ color: END_OF_DATE_REPORT_PAYMENT_COLORS[dataEnum], fontWeight: 'bold' }}
                    suffix="₺"
                  />
                </Card>
              </Col>
            );
          },
        )}
      </Row>
    );
  };

  const storesSelectOptionsList = stores.map((store) => {
    return { value: store.code, label: `${store.code} - ${store.name}` };
  });

  return (
    <>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col span={8}>
          <Form.Item name="range-picker" style={{ marginBottom: '10px' }}>
            <ConfigProvider locale={trTR}>
              <DatePicker.RangePicker
                style={{ width: '90%' }}
                allowClear={false}
                format={'YYYY-MM-DD'}
                onChange={datepickerChangeHandler}
              />
            </ConfigProvider>
          </Form.Item>
        </Col>
        <Col span={8}>
          {!selectedActiveStoreCode && (
            <Form.Item
              name="storeCode"
              rules={[{ required: true, message: t('requiredStore') }]}
            >
              <Select
                showSearch
                allowClear
                loading={isStoresFetching}
                placeholder={t('store')}
                style={{ width: '100%' }}
                onChange={(value) =>
                  setEndOfDayReportForm((prevState) => ({
                    ...prevState,
                    storeCode: value,
                  }))
                }
                options={storesSelectOptionsList}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}
        </Col>
        <Col span={8} style={{ display: 'flex', justifyContent: 'end' }}>
          {reportData && (
            <Button
              shape="round"
              icon={<FilterOutlined />}
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            >
              {t('filters')}
            </Button>
          )}
        </Col>
      </Row>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
        }}
      >
        {renderSelectedFilters()}
      </Row>
      {!reportData ? (
        fetching ? (
          <Loading />
        ) : (
          <p>{t('alert.selectDateRange')}</p>
        )
      ) : null}
      {reportData && (
        <>
          {renderSummaryBoxes()}
          <Table
            loading={fetching}
            size="small"
            style={{ width: '100%' }}
            columns={columns}
            dataSource={reportData?.endOfDayReportDtos}
            rowKey={(record) => record.index}
            pagination={{
              showSizeChanger: false,
              pageSize: 50,
            }}
            scroll={{
              y: '60vh',
            }}
          />
        </>
      )}
      <Suspense fallback={() => { }}>
        {isFiltersOpen && (
          <EndOfDayReportFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            setFilterHeaders={setFilterHeaders}
            form={form}
            selectedStoreCode={endOfDayReportForm.storeCode}
          />
        )}
      </Suspense>
    </>
  );
};

export default EndOfDayReport;
